import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Badge, Button, Input } from "@/components/ui";
import { fmtDateTime } from "@/lib/dates";

type Rewards = { enabled: boolean; balance: number; value: string; point_value: string; min_redeem: number; last_earn_at: string | null; ledger: { id: string; delta: number; reason: string; note: string | null; at: string; balance_after: number }[] };

/** Customer drawer (P13): points balance, its € value, the ledger, and a manual adjustment for managers and above. */
export function CustomerRewards({ customerId }: { customerId: string }) {
  const { t, i18n } = useTranslation();
  const { me } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["rewards", customerId], queryFn: () => api<Rewards>(`/app/customers/${customerId}/rewards`) });
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const canAdjust = !!me?.roles.some((r) => ["owner", "admin", "manager"].includes(r.role));
  const adjust = useMutation({
    mutationFn: () => api(`/app/customers/${customerId}/rewards/adjust`, { method: "POST", body: JSON.stringify({ delta: Number(delta), note: note || null }) }),
    onSuccess: () => { setDelta(""); setNote(""); setError(null); qc.invalidateQueries({ queryKey: ["rewards", customerId] }); qc.invalidateQueries({ queryKey: ["customers-list"] }); },
    onError: (e) => { let reason = e instanceof ApiError ? e.detail : ""; try { reason = JSON.parse(reason).reason; } catch { /* plain */ } setError(t(`rw.err.${reason}`, { defaultValue: reason || t("app.error") })); },
  });
  if (!q.data || !q.data.enabled) return null;
  const d = q.data;
  return (
    <div className="space-y-2 rounded-lg border p-2">
      <div className="flex flex-wrap items-center gap-2"><span className="font-medium">{t("rw.points")}</span><Badge tone="accent">{d.balance}</Badge><span className="text-xs text-muted">≈ {d.value} €{d.min_redeem ? ` · ${t("rw.minRedeemShort", { n: d.min_redeem })}` : ""}</span></div>
      {d.ledger.length > 0 && (
        <table className="w-full text-xs"><tbody>{d.ledger.slice(0, 12).map((r) => (
          <tr key={r.id} className="border-t"><td className="py-1 text-muted">{fmtDateTime(r.at, i18n.language)}</td><td>{t(`rw.reason.${r.reason}`, { defaultValue: r.reason })}{r.note ? <span className="text-muted"> · {r.note}</span> : null}</td><td className={`text-right font-mono ${r.delta < 0 ? "text-danger" : "text-ok"}`}>{r.delta > 0 ? `+${r.delta}` : r.delta}</td><td className="text-right font-mono text-muted">{r.balance_after}</td></tr>))}</tbody></table>
      )}
      {canAdjust && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-2 text-xs">
          <Input className="!w-24" type="number" placeholder="±" value={delta} onChange={(e) => setDelta(e.target.value)} />
          <Input className="!w-48" placeholder={t("customers.adjustNote")} value={note} onChange={(e) => setNote(e.target.value)} />
          <Button type="button" variant="secondary" size="sm" disabled={!Number(delta) || adjust.isPending} onClick={() => adjust.mutate()}>{t("customers.adjust")}</Button>
          {error && <span className="text-danger">{error}</span>}
        </div>
      )}
    </div>
  );
}
